import { useState, useEffect } from 'react';
import { Trash2, Heart, ShoppingBag, ArrowRight, Tag, ChevronRight } from 'lucide-react';

const cur = () => window.themeData?.currency ?? '$';

function QtyControl({ value, onChange }) {
  return (
    <div className="flex items-center bg-gray-50 rounded-2xl border border-gray-200 p-2">
      <button
        onClick={() => onChange(Math.max(1, value - 1))}
        className="w-12 h-12 rounded-xl flex items-center justify-center text-gray-500 hover:bg-white hover:text-primary transition-all font-bold text-xl"
      >—</button>
      <input
        type="number"
        value={value}
        min="1"
        onChange={e => onChange(Math.max(1, parseInt(e.target.value) || 1))}
        className="w-12 text-center font-bold text-lg bg-transparent border-none outline-none text-textDark appearance-none m-0 p-0"
      />
      <button
        onClick={() => onChange(value + 1)}
        className="w-12 h-12 rounded-xl flex items-center justify-center text-gray-500 hover:bg-white hover:text-primary transition-all font-bold text-xl"
      >+</button>
    </div>
  );
}

export default function CartPage() {
  const [items,   setItems]   = useState([]);
  const [totals,  setTotals]  = useState(null);
  const [coupon,  setCoupon]  = useState('');
  const [loading, setLoading] = useState(true);

  const td     = window.themeData ?? {};
  const base   = (td.apiUrl ?? '').replace(/\/$/, '');
  const HDRS   = { 'Content-Type': 'application/json', 'Nonce': td.wcStoreNonce ?? '' };

  async function fetchCart() {
    try {
      const r    = await fetch(`${base}/wc/store/v1/cart`, { credentials: 'same-origin', headers: HDRS });
      const data = await r.json();
      setItems((data.items ?? []).map(item => ({
        key:   item.key,
        id:    item.id,
        name:  item.name,
        image: item.images?.[0]?.src ?? null,
        price: parseFloat(item.prices?.price ?? 0) / 100,
        qty:   item.quantity,
        meta:  item.variation ?? [],
      })));
      setTotals(data.totals ?? null);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchCart(); }, []);

  async function updateQty(key, qty) {
    setItems(prev => prev.map(i => i.key === key ? { ...i, qty } : i));
    await fetch(`${base}/wc/store/v1/cart/update-item`, {
      method: 'POST', headers: HDRS,
      body: JSON.stringify({ key, quantity: qty }),
    }).catch(() => {});
    fetchCart();
  }

  async function removeItem(key) {
    setItems(prev => prev.filter(i => i.key !== key));
    await fetch(`${base}/wc/store/v1/cart/remove-item`, {
      method: 'POST', headers: HDRS,
      body: JSON.stringify({ key }),
    }).catch(() => {});
    fetchCart();
  }

  const div       = Math.pow(10, totals?.currency_minor_unit ?? 2);
  const subtotal  = items.reduce((s, i) => s + i.price * i.qty, 0);
  const tax       = totals ? parseFloat(totals.total_tax ?? 0) / div : 0;
  const rawLines  = (totals?.tax_lines ?? []).filter(tl => parseFloat(tl.tax_total ?? 0) > 0);
  // WC sometimes merges CGST+SGST into one "GST" entry — detect and split
  const GST_SPECIFIC = ['CGST', 'SGST', 'IGST'];
  const alreadySplit = rawLines.length >= 2 ||
    (rawLines.length === 1 && GST_SPECIFIC.includes((rawLines[0]?.name ?? '').toUpperCase()));
  const taxLines = alreadySplit ? rawLines : null; // null = needs manual split
  const shipping  = subtotal >= 1999 ? 0 : 70;
  const total     = subtotal + tax + shipping;
  const checkoutUrl = `${td.siteUrl ?? ''}/checkout/`;
  const shopUrl     = `${td.siteUrl ?? ''}/shop/`;

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 flex flex-col items-center justify-center min-h-[50vh]">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-textLight font-bold">Loading your cart…</p>
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8 pb-20">
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-textDark mb-12">Your Cart</h1>
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-16 flex flex-col items-center justify-center text-center">
          <div className="w-28 h-28 bg-primary/5 rounded-full flex items-center justify-center mb-6">
            <ShoppingBag className="w-12 h-12 text-primary" />
          </div>
          <h2 className="text-2xl font-black text-textDark mb-3">Your cart is empty</h2>
          <p className="text-textLight mb-8">Looks like you haven't added anything yet!</p>
          <a href={shopUrl} className="inline-flex items-center gap-2 bg-primary hover:bg-primaryHover text-white font-black px-8 py-4 rounded-2xl shadow-soft transition-colors">
            Start Shopping <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 pt-8">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-textDark">Your Cart</h1>
        <span className="text-gray-500 font-bold bg-gray-100 px-6 py-2 rounded-xl text-lg">{items.length} Item{items.length !== 1 ? 's' : ''}</span>
      </div>

      <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-start">

        {/* Cart items */}
        <div className="w-full lg:w-2/3 flex flex-col gap-6">
          {items.map(item => (
            <div key={item.key} className="bg-white rounded-3xl shadow-sm border border-gray-100 p-5 md:p-8 flex flex-col md:flex-row gap-6 relative group">
              {/* Image */}
              <div className="w-full md:w-40 h-48 md:h-40 bg-gray-50 rounded-2xl p-4 flex-shrink-0 flex items-center justify-center">
                {item.image
                  ? <img src={item.image} alt={item.name} className="w-full h-full object-contain" />
                  : <span className="text-6xl">🎁</span>
                }
              </div>

              {/* Info */}
              <div className="flex-grow flex flex-col justify-between">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-bold text-xl md:text-2xl text-textDark hover:text-primary transition-colors cursor-pointer">{item.name}</h3>
                    {item.meta?.length > 0 && (
                      <p className="text-sm text-gray-500 mt-1">{item.meta.map(m => `${m.attribute}: ${m.value}`).join(' • ')}</p>
                    )}
                  </div>
                  <button
                    onClick={() => removeItem(item.key)}
                    className="text-gray-400 hover:text-red-500 transition-colors p-3 rounded-xl hover:bg-red-50 min-w-[44px] min-h-[44px] flex items-center justify-center"
                    aria-label="Remove item"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-4 mt-auto">
                  <QtyControl value={item.qty} onChange={qty => updateQty(item.key, qty)} />
                  <div className="text-right">
                    <span className="font-black text-2xl md:text-3xl text-primary">{cur()}{(item.price * item.qty).toFixed(2)}</span>
                    {item.qty > 1 && <p className="text-xs text-gray-400">{cur()}{item.price.toFixed(2)} each</p>}
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-gray-50">
                  <button className="text-sm text-textLight hover:text-accent1 font-bold flex items-center gap-2 transition-colors py-1.5 px-3 -ml-3 rounded-xl hover:bg-accent1/5">
                    <Heart className="w-4 h-4" /> Save for later
                  </button>
                </div>
              </div>
            </div>
          ))}

          {/* Coupon */}
          <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 md:p-8">
            <h3 className="font-bold text-lg text-textDark mb-4 flex items-center gap-2">
              <Tag className="w-5 h-5 text-primary" /> Promo Code
            </h3>
            <div className="flex gap-3">
              <input
                type="text"
                value={coupon}
                onChange={e => setCoupon(e.target.value)}
                placeholder="Enter code…"
                className="flex-grow bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-textDark focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all font-bold"
              />
              <button className="bg-primary hover:bg-primaryHover text-white font-black px-6 py-3 rounded-xl transition-colors shadow-soft">
                Apply
              </button>
            </div>
          </div>
        </div>

        {/* Order summary */}
        <div className="w-full lg:w-1/3 lg:sticky lg:top-28 self-start">
          <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 md:p-8">
            <h2 className="text-2xl font-black text-textDark mb-6">Order Summary</h2>

            <div className="space-y-4 mb-6">
              <div className="flex justify-between text-base text-gray-600 font-bold">
                <span>Subtotal ({items.length} items)</span>
                <span>{cur()}{subtotal.toFixed(2)}</span>
              </div>
              {tax > 0 && (
                taxLines
                  ? taxLines.map((tl, i) => (
                      <div key={i} className="flex justify-between text-base text-gray-600 font-bold">
                        <span>{tl.name}</span>
                        <span>{cur()}{(parseFloat(tl.tax_total) / div).toFixed(2)}</span>
                      </div>
                    ))
                  : <>
                      <div className="flex justify-between text-base text-gray-600 font-bold">
                        <span>CGST</span>
                        <span>{cur()}{(tax / 2).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-base text-gray-600 font-bold">
                        <span>SGST</span>
                        <span>{cur()}{(tax / 2).toFixed(2)}</span>
                      </div>
                    </>
              )}
              <div className="flex justify-between text-base text-gray-600 font-bold">
                <span>Shipping</span>
                <span className={shipping === 0 ? 'text-accent3 font-black' : ''}>{shipping === 0 ? 'FREE 🎉' : `${cur()}${shipping}`}</span>
              </div>
              {shipping > 0 && (
                <div className="space-y-1.5">
                  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-primary to-accent1 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(100, (subtotal / 1999) * 100).toFixed(1)}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-400">Add {cur()}{(1999 - subtotal).toFixed(0)} more for free shipping</p>
                </div>
              )}
              <div className="border-t border-gray-100 pt-4 flex justify-between items-baseline">
                <span className="font-black text-xl text-textDark">Total</span>
                <span className="font-black text-3xl text-primary">{cur()}{total.toFixed(2)}</span>
              </div>
            </div>

            <a
              href={checkoutUrl}
              className="group w-full bg-primary hover:bg-primaryHover text-white font-black text-lg py-4 px-6 rounded-2xl shadow-soft transition-all flex items-center justify-center gap-3"
            >
              Proceed to Checkout
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>

            <a href={shopUrl} className="mt-4 w-full text-center block text-textLight hover:text-primary font-bold text-sm transition-colors py-2">
              ← Continue Shopping
            </a>

            {/* Trust badges */}
            <div className="mt-6 pt-6 border-t border-gray-50 grid grid-cols-3 gap-3 text-center">
              {[['🔒', 'Secure'], ['↩', '30-day returns'], ['🚚', 'Fast Delivery']].map(([icon, label]) => (
                <div key={label} className="flex flex-col items-center gap-1">
                  <span className="text-xl">{icon}</span>
                  <span className="text-xs text-gray-500 font-bold">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
